import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { TCLIconImage } from '../../components/TCLMark';
import {
  LayoutDashboard, FileText, Image, Calendar, MessageSquare, Headphones,
  Users, Mail, Settings, LogOut, ChevronLeft, Bot, Menu, X, BarChart3, CalendarDays, Link2, Layers, ClipboardCheck,
  Radio, UserPlus, Send, Activity, LayoutGrid, CreditCard, History, MessageCircle, ShieldCheck, Inbox,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Home', exact: true },
  { path: '/admin/sessions', icon: Radio, label: 'Sessions' },
  { path: '/admin/speakers', icon: UserPlus, label: 'Speakers' },
  { path: '/admin/cfq', icon: ClipboardCheck, label: 'CFQ Analytics' },
  { path: '/admin/mailing', icon: Mail, label: 'List' },
  { path: '/admin/nurture', icon: Activity, label: 'Nurture' },
  { path: '/admin/inbox', icon: Inbox, label: 'Inbox', badgeKey: 'unreadEmails' },
  { path: '/admin/payments', icon: CreditCard, label: 'Payments' },
  { path: '/admin/readers', icon: ShieldCheck, label: 'Reader Access' },
  { path: '/admin/quote-cards', icon: LayoutGrid, label: 'Quote Cards' },
  { path: '/admin/compose', icon: Send, label: 'Compose' },
  { path: '/admin/articles', icon: FileText, label: 'Articles' },
  { path: '/admin/comments', icon: MessageCircle, label: 'Comments', badge: true },
  { path: '/admin/pages', icon: Layers, label: 'Pages' },
  { path: '/admin/about', icon: FileText, label: 'About Page' },
  { path: '/admin/media', icon: Image, label: 'Media' },
  { path: '/admin/events', icon: Calendar, label: 'Events' },
  { path: '/admin/social', icon: MessageSquare, label: 'Social Hub' },
  { path: '/admin/podcast', icon: Headphones, label: 'Podcast' },
  { path: '/admin/bio-links', icon: Link2, label: 'Bio Links' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/admin/calendar', icon: CalendarDays, label: 'Calendar' },
  { path: '/admin/crawlers', icon: Bot, label: 'SEO & Crawlers' },
  { path: '/admin/audit', icon: History, label: 'Audit Log' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

const AdminLayout = () => {
  const { admin, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingComments, setPendingComments] = useState(0);
  const [unreadEmails, setUnreadEmails] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !isAuthenticated()) return;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`${API_URL}/api/hostinger-email/inbox?page=1&page_size=1`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setUnreadEmails(d.unread_count || 0); }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isAuthenticated()) return;
    const fetchPending = async () => {
      try {
        const r = await fetch(`${API_URL}/api/cms/comments/stats`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setPendingComments(d.pending || 0); }
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated || !isAuthenticated()) return null;

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-navy-deep flex" data-testid="admin-layout">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 bg-navy border-r border-gold/[0.06] flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-56'} hidden lg:flex`}>
        {/* Brand */}
        <div className="h-14 flex items-center px-4 border-b border-gold/[0.06]">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2">
              <TCLIconImage size={22} />
              <span className="font-serif text-sm"><span className="text-gold">TCL</span> <span className="text-cream/60">Admin</span></span>
            </Link>
          )}
          {collapsed && <TCLIconImage size={22} />}
          <button onClick={() => setCollapsed(!collapsed)} className={`${collapsed ? 'mx-auto' : 'ml-auto'} p-1 text-cream/30 hover:text-gold transition-colors`} data-testid="sidebar-toggle">
            <ChevronLeft size={16} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                  active
                    ? 'bg-gold/[0.1] text-gold border border-gold/[0.15]'
                    : 'text-cream/40 hover:text-cream/70 hover:bg-cream/[0.03] border border-transparent'
                } ${collapsed ? 'justify-center px-2' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={16} className="flex-shrink-0" />
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {!collapsed && item.badge && pendingComments > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium leading-none">
                    {pendingComments}
                  </span>
                )}
                {!collapsed && item.badgeKey === 'unreadEmails' && unreadEmails > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-400 font-medium leading-none">
                    {unreadEmails}
                  </span>
                )}
                {collapsed && item.badge && pendingComments > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-400" />
                )}
                {collapsed && item.badgeKey === 'unreadEmails' && unreadEmails > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-teal-400" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="border-t border-gold/[0.06] p-3">
          {!collapsed && (
            <div className="mb-2 px-1">
              <p className="text-cream/60 text-xs font-medium truncate">{admin?.first_name} {admin?.last_name}</p>
              <p className="text-cream/25 text-[10px] truncate">{admin?.email}</p>
            </div>
          )}
          <button onClick={handleLogout} className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-cream/30 hover:text-red-400 hover:bg-red-500/[0.05] transition-all text-sm ${collapsed ? 'justify-center px-2' : ''}`} data-testid="admin-logout-btn">
            <LogOut size={14} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-navy border-b border-gold/[0.06] flex items-center px-4">
        <button onClick={() => setMobileOpen(true)} className="p-2 text-cream/50" data-testid="mobile-admin-menu">
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 ml-3">
          <TCLIconImage size={20} />
          <span className="font-serif text-sm text-cream/70">Admin</span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-navy border-r border-gold/[0.06] flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b border-gold/[0.06]">
              <span className="font-serif text-sm"><span className="text-gold">TCL</span> <span className="text-cream/60">Admin</span></span>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-cream/30"><X size={18} /></button>
            </div>
            <nav className="flex-1 py-3 overflow-y-auto">
              {navItems.map((item) => {
                const active = isActive(item.path, item.exact);
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)} className={`flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-sm transition-all ${active ? 'bg-gold/[0.1] text-gold' : 'text-cream/40 hover:text-cream/70'}`}>
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="border-t border-gold/[0.06] p-3">
              <button onClick={handleLogout} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-cream/30 hover:text-red-400 text-sm">
                <LogOut size={14} /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-56'} pt-14 lg:pt-0`}>
        <div className="min-h-screen p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
